/**
 * Context Mediator - Studio v3.6 Sprint 2
 *
 * Single interception point between icon clicks and canvas updates.
 * Per GUARD.md and Sprint 2 requirements:
 * - Icons emit suggestions, never mutate canvas directly in Auto mode
 * - Soma mediation is explicit and testable
 * - Manual Mode bypass is clean, not hacky
 *
 * This store is intentionally dumb - it only stores pending suggestions.
 * Resolution happens in useContextMediationEffect.
 */

import { create } from 'zustand';
import type { StudioPanelDescriptor } from '../panels/registry.js';

/**
 * Context suggestion emitted by icons/controls
 */
export interface ContextSuggestion {
    /** Domain/app ID being suggested */
    domainId: string;
    /** Source of the suggestion */
    source: 'icon' | 'chat' | 'system';
    /** Timestamp when suggestion was created */
    timestamp: number;
    /** Optional panel descriptor for routing info */
    panelDescriptor?: StudioPanelDescriptor;
    /** Optional route pattern override */
    routePattern?: string;
    /** Optional label for narration */
    label?: string;
}

/**
 * Context mediator state
 *
 * Intentionally minimal - this store only tracks pending suggestions.
 * It does NOT know about:
 * - Canvas state
 * - Chat state
 * - Manual mode
 * - Execution logic
 *
 * Resolution happens in useContextMediationEffect.
 */
interface ContextMediatorState {
    /** Current pending suggestion (null if none) */
    pendingSuggestion: ContextSuggestion | null;

    /** Emit a context suggestion (called by icons) */
    suggestContext: (
        domainId: string,
        source: ContextSuggestion['source'],
        options?: Partial<Omit<ContextSuggestion, 'domainId' | 'source' | 'timestamp'>>
    ) => void;

    /** Clear the pending suggestion (called after processing) */
    clearSuggestion: () => void;

    /** Get the current suggestion (for testing) */
    getSuggestion: () => ContextSuggestion | null;
}

/**
 * Context mediator store
 *
 * Usage:
 * 1. Icons call suggestContext(domainId, 'icon')
 * 2. useContextMediationEffect subscribes to pendingSuggestion
 * 3. Effect processes suggestion based on manualMode
 * 4. Effect calls clearSuggestion() after processing
 */
export const useContextMediatorStore = create<ContextMediatorState>((set, get) => ({
    pendingSuggestion: null,

    suggestContext: (domainId, source, options = {}) => {
        const suggestion: ContextSuggestion = {
            domainId,
            source,
            timestamp: Date.now(),
            ...options,
        };

        set({ pendingSuggestion: suggestion });

        // Dev warning: log when suggestions are emitted
        if (process.env.NODE_ENV === 'development') {
            console.debug('[ContextMediator] Suggestion emitted:', suggestion);
        }
    },

    clearSuggestion: () => {
        set({ pendingSuggestion: null });
    },

    getSuggestion: () => get().pendingSuggestion,
}));

/**
 * Hook to emit context suggestions
 *
 * Use this in icon/button components instead of direct navigation.
 */
export function useSuggestContext() {
    const suggestContext = useContextMediatorStore(state => state.suggestContext);
    return suggestContext;
}

/**
 * Hook to access pending suggestion (for mediation effect)
 */
export function usePendingSuggestion() {
    const pendingSuggestion = useContextMediatorStore(state => state.pendingSuggestion);
    const clearSuggestion = useContextMediatorStore(state => state.clearSuggestion);
    return { pendingSuggestion, clearSuggestion };
}

export default useContextMediatorStore;
